import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import GetAppIcon from "@mui/icons-material/GetApp";
import Loader from "components/Loader/Loader";
import { useLanguage } from "context/LanguageContext";
import api from "services/api";

const DEFAULT_INSTRUCTIONS = `Given the invoice document provided, extract all relevant information and structure the output as detailed JSON that matches the database schema for storing invoice headers and line items. The invoice headers should include Total Amount, Total Tax Amount, Net Amount, Invoice Date, Invoice ID, Receiver Tax ID, Supplier Tax ID, Receiver Name, Supplier Name, Currency, Supplier Address, Receiver Company Code, Supplier IBAN, Supplier Company Code, Due date, VAT Percentage, Discount Amount, and Discount Percentage. 

The line items should include SKU, description, quantity, unit price, and unit of measure for each item on the invoice (consider the price after discount as the unit price if available). Exclude any sensitive information from the output. Use the following alternative names for fields where necessary: Company Code, Business Code, Receiver Business Code, Supplier Business Code. Ensure all dates are in the format YYYY-MM-DD, Total Amount, Total Tax Amount, Net Amount, and Discount Amount are in the format xx.xx with two decimal places, and all other numbers are in the format xx.xxxx with four decimal places, even if the decimal part is zero.

Extract invoice IDs by identifying patterns such as 'Serija [Series] Nr. [Number]' or 'nr. [Series] [Number]'. Remove the 'nr' prefix from the result, but retain any '/' characters present in the invoice number. VAT Percentage and Discount Percentage should be integers (e.g., 5, 21, 16). 

The invoice is a structured representation where data is provided in distinct blocks:
1. Supplier and receiver details may appear either side by side (horizontally) or in vertical blocks. Ensure all details from both blocks are extracted regardless of their layout.
2. Line items are always presented in a vertical table format, with each row representing an individual item. Ensure fields like SKU, description, quantity, unit price, and unit of measure are accurately extracted.

When processing the invoice:
- Treat all sections (headers, supplier/receiver details, and line items) as distinct logical blocks.
- Use the provided schema to organize the data logically in JSON format.
- Align field labels with their respective JSON keys, even if alternative field names are present in the invoice (e.g., Business Code for Company Code).

Return only the JSON structure as specified below, without any additional text, comments, or explanations.

Format the JSON as follows:

{
  "invoice_headers": {
    "total_amount": 0.00,
    "total_tax_amount": 0.00,
    "net_amount": 0.00,
    "invoice_date": "YYYY-MM-DD",
    "invoice_id": "[Series][Number]",
    "receiver_tax_id": "",
    "supplier_tax_id": "",
    "receiver_name": "",
    "supplier_name": "",
    "currency": "",
    "supplier_address": "",
    "receiver_company_code": "",
    "supplier_iban": "",
    "supplier_company_code": "",
    "due_date": "YYYY-MM-DD",
    "vat_percentage": 0,
    "discount_amount": 0.00,
    "discount_percentage": 0
  },
  "line_items": [
    {
      "sku": "",
      "description": "",
      "quantity": 0.0000,
      "unit_price": 0.0000,
      "unit_of_measure": ""
    }
  ]
}`;

const DEFAULT_ADDITIONAL_INFO = `Please ensure that the extracted data correctly identifies the supplier and buyer. The supplier is usually the issuer of the invoice, and the buyer is the recipient. Use the provided company code as the receiver's company code to identify the buyer in the invoice document.

When extracting line items, prioritize information from Invoice Tables (JSON), where the data is typically more structured and reliable. Only refer to the text for line items if no relevant table is found. This applies to extracting SKU, description, quantity, unit price, and unit of measure. Tables are usually labeled or are located in close proximity to section headers like 'Items', 'Description', 'Products', or similar. 

Extract all numerical amounts (total amount, total tax amount, net amount, quantity, unit price, and unit of measure) directly from the recognized text or tables without generating or assuming any values. Ensure that all dates are in the format YYYY-MM-DD.

Extract the quantity values strictly from the column labeled 'Quantity', and ensure they are numeric values without any units or additional text. Ignore any other columns such as 'Packaging', 'Units', or any other text that is not purely numeric in the 'Quantity' column. The 'Quantity' column should only contain numeric values.

Recognize and extract the currency information from the invoice. Currency can be represented as currency codes (such as USD, EUR, GBP) or currency symbols (such as $, €, £). Ensure that all extracted currency values are properly identified and associated with the correct currency code or symbol. For instance, if the amount is "12.8017 Eur", recognize "Eur" as the currency code for Euro and "€" as the Euro symbol. Similarly, recognize "$" for USD, "£" for GBP, and other common currency symbols and codes.

The invoice date should not be used multiple times; it should be distinct from the due date. Use due date field appropriately and ensure invoice date and due date are different if provided.

Ensure that the supplier's details such as name, address, tax ID, and company code are accurately extracted and assigned correctly. Similarly, ensure that the receiver's details are also extracted and assigned correctly using the provided company code for identification.

Typically, the company name, company code, and VAT code are grouped together or located close to each other. Use this context to accurately identify and extract these details.

If the document contains multiple invoices, ensure each invoice is extracted and structured separately within the JSON format. Each invoice should have its own distinct header and line items.

Verify that the VAT Percentage is extracted as an integer, and if multiple VAT rates are present, list them separately within the JSON structure. If VAT is not explicitly stated, calculate it based on the total amount and net amount if possible, otherwise leave it empty.

Validate that all extracted data adheres to the specified formats and correct any formatting issues where necessary. For example, ensure all monetary values have two decimal places and all other numerical values have four decimal places.

Use the following patterns to recognize and extract VAT codes for different countries:
    r'^(ATU\\d{8})$|'           # Austria
    r'^(BE0\\d{9})$|'           # Belgium
    r'^(BG\\d{9,10})$|'         # Bulgaria
    r'^(CY\\d{8}[A-Z])$|'       # Cyprus
    r'^(CZ\\d{8,10})$|'         # Czech Republic
    r'^(DE\\d{9})$|'            # Germany
    r'^(DK\\d{8})$|'            # Denmark
    r'^(EE\\d{9})$|'            # Estonia
    r'^(EL\\d{9})$|'            # Greece
    r'^(ES[A-Z]\\d{7}[A-Z]|\\d{8}[A-Z])$|' # Spain
    r'^(FI\\d{8})$|'            # Finland
    r'^(FR[A-HJ-NP-Z]\\d{2}\\d{9})$|' # France
    r'^(HR\\d{11})$|'           # Croatia
    r'^(HU\\d{8})$|'            # Hungary
    r'^(IE\\d{7}[A-Z]{1,2})$|'  # Ireland
    r'^(IT\\d{11})$|'           # Italy
    r'^(LT\\d{9,12})$|'         # Lithuania
    r'^(LU\\d{8})$|'            # Luxembourg
    r'^(LV\\d{11})$|'           # Latvia
    r'^(MT\\d{8})$|'            # Malta
    r'^(NL\\d{9}B\\d{2})$|'     # Netherlands
    r'^(PL\\d{10})$|'           # Poland
    r'^(PT\\d{9})$|'            # Portugal
    r'^(RO\\d{2,10})$|'         # Romania
    r'^(SE\\d{12})$|'           # Sweden
    r'^(SI\\d{8})$|'            # Slovenia
    r'^(SK\\d{10})$'            # Slovakia

Pay special attention to the context in which the codes appear to accurately identify and assign them. Typically, VAT codes will be near labels such as 'PVM mokėtojo kodas', 'VAT ID', 'VAT Code'. If multiple VAT codes are present, use the context to determine which one belongs to the supplier and which to the receiver.
The provided invoice text and tables are parts of the same document and must be used together for data extraction. Neither source should take precedence over the other by default. Use both sources to ensure all relevant fields (header and line items) are captured accurately and completely, filling in any missing data from one source using the other. If the same data is present in both sources, ensure consistency by cross-checking the values.`;

const LLMSettings = ({ companyId }) => {
  const { t } = useLanguage();
  const [settings, setSettings] = useState({
    azure_endpoint: "",
    azure_api_key: "",
    content_understanding_endpoint: "",
    content_understanding_api_key: "",
    content_understanding_analyzer_id: "prebuilt-invoice",
    content_understanding_api_version: "2025-11-01",
    anthropic_api_key: "",
    openai_api_key: "",
    google_api_key: "",
    llm_instructions: DEFAULT_INSTRUCTIONS,
    additional_info: DEFAULT_ADDITIONAL_INFO,
    default_model: "openai",
    openai_model_version: "",
    claude_model_version: "",
    gemini_model_version: "",
    use_openai_vision: false,
    openai_vision_prompt: "",
    // Dual-Pass settings
    enable_dual_pass: false,
    dual_pass_primary_model: "claude",
    dual_pass_primary_model_version: "",
    dual_pass_validation_model: "openai",
    dual_pass_validation_model_version: "",
    dual_pass_validation_prompt: "",
    // Product Matching settings
    product_match_model: "openai",
    product_match_model_version: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [userCompanies, setUserCompanies] = useState([]);
  const [selectedSourceCompany, setSelectedSourceCompany] = useState("");
  const [importLoading, setImportLoading] = useState(false);

  useEffect(() => {
    if (settings.default_model && settings.default_model !== "openai") {
      if (settings.use_openai_vision) {
        setSettings((prevSettings) => ({
          ...prevSettings,
          use_openai_vision: false,
        }));
      }
    }
  }, [settings.default_model, settings.use_openai_vision]);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!companyId) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/api/companies/${companyId}/llm-settings/`);
        if (response.data) {
          const cleanedData = Object.keys(response.data).reduce((acc, key) => {
            acc[key] = response.data[key] === null ? "" : response.data[key];
            return acc;
          }, {});

          cleanedData.use_openai_vision =
            typeof cleanedData.use_openai_vision === "boolean"
              ? cleanedData.use_openai_vision
              : cleanedData.use_openai_vision === "true";

          cleanedData.enable_dual_pass =
            typeof cleanedData.enable_dual_pass === "boolean"
              ? cleanedData.enable_dual_pass
              : cleanedData.enable_dual_pass === "true";

          const currentDefaultModelVersion = cleanedData.model_version || "";
          cleanedData.openai_model_version =
            cleanedData.openai_model_version ||
            (cleanedData.default_model === "openai" ? currentDefaultModelVersion : "");
          cleanedData.claude_model_version =
            cleanedData.claude_model_version ||
            (cleanedData.default_model === "claude" ? currentDefaultModelVersion : "");
          cleanedData.gemini_model_version =
            cleanedData.gemini_model_version ||
            (cleanedData.default_model === "gemini" ? currentDefaultModelVersion : "");

          if (!cleanedData.content_understanding_analyzer_id) {
            cleanedData.content_understanding_analyzer_id = "prebuilt-invoice";
          }

          if (!cleanedData.content_understanding_api_version) {
            cleanedData.content_understanding_api_version = "2025-11-01";
          }

          // Set default dual-pass model versions if not set
          if (!cleanedData.dual_pass_primary_model_version) {
            const primaryModel = cleanedData.dual_pass_primary_model || "claude";
            cleanedData.dual_pass_primary_model_version =
              primaryModel === "claude"
                ? "claude-3-5-haiku-20241022"
                : primaryModel === "openai"
                ? "gpt-4o-mini-2024-07-18"
                : primaryModel === "gemini"
                ? "gemini-2.0-flash"
                : "";
          }

          if (!cleanedData.dual_pass_validation_model_version) {
            const validationModel = cleanedData.dual_pass_validation_model || "openai";
            cleanedData.dual_pass_validation_model_version =
              validationModel === "claude"
                ? "claude-3-5-haiku-20241022"
                : validationModel === "openai"
                ? "gpt-4o-mini-2024-07-18"
                : validationModel === "gemini"
                ? "gemini-2.0-flash"
                : "";
          }

          // Set default product match model version if not set
          if (!cleanedData.product_match_model_version) {
            const matchModel = cleanedData.product_match_model || "openai";
            cleanedData.product_match_model_version =
              matchModel === "claude"
                ? "claude-3-5-haiku-20241022"
                : matchModel === "openai"
                ? "gpt-4o-mini-2024-07-18"
                : matchModel === "gemini"
                ? "gemini-2.0-flash"
                : "";
          }

          console.log("Loaded llm_instructions:", cleanedData.llm_instructions);

          setSettings(cleanedData);
        }
        setLoading(false);
      } catch (err) {
        setError("Failed to load LLM settings");
        setLoading(false);
      }
    };

    fetchSettings();
  }, [companyId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let valToSet = type === "checkbox" ? checked : value;
    if (name === "use_openai_vision" || name === "enable_dual_pass") {
      valToSet = value === "true" || value === true;
    }
    setSettings((prev) => ({
      ...prev,
      [name]: valToSet,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyId) {
      setError("No company selected");
      return;
    }

    try {
      const { id, company, created_at, updated_at, ...submitData } = settings;
      submitData.model_version =
        settings.default_model === "openai"
          ? settings.openai_model_version || ""
          : settings.default_model === "claude"
          ? settings.claude_model_version || ""
          : settings.default_model === "gemini"
          ? settings.gemini_model_version || ""
          : "";

      submitData.openai_model_version = settings.openai_model_version || "";
      submitData.claude_model_version = settings.claude_model_version || "";
      submitData.gemini_model_version = settings.gemini_model_version || "";
      submitData.use_openai_vision =
        typeof settings.use_openai_vision === "boolean"
          ? settings.use_openai_vision
          : settings.use_openai_vision === "true";

      submitData.enable_dual_pass =
        typeof settings.enable_dual_pass === "boolean"
          ? settings.enable_dual_pass
          : settings.enable_dual_pass === "true";

      // Dual-pass model versions
      submitData.dual_pass_primary_model_version = settings.dual_pass_primary_model_version || "";
      submitData.dual_pass_validation_model_version =
        settings.dual_pass_validation_model_version || "";
      submitData.dual_pass_validation_prompt = settings.dual_pass_validation_prompt || "";

      // Product matching model version
      submitData.product_match_model_version = settings.product_match_model_version || "";

      console.log("Submitting settings:", submitData);

      const response = await api.put(`/api/companies/${companyId}/llm-settings/`, submitData);

      if (response.data) {
        const returnedData = response.data;
        setSettings((prev) => ({
          ...prev,
          ...returnedData,
          openai_model_version: prev.openai_model_version,
          claude_model_version: prev.claude_model_version,
          gemini_model_version: prev.gemini_model_version,
          dual_pass_primary_model_version: prev.dual_pass_primary_model_version,
          dual_pass_validation_model_version: prev.dual_pass_validation_model_version,
          product_match_model_version: prev.product_match_model_version,
          use_openai_vision:
            typeof returnedData.use_openai_vision === "boolean"
              ? returnedData.use_openai_vision
              : returnedData.use_openai_vision === "true",
          enable_dual_pass:
            typeof returnedData.enable_dual_pass === "boolean"
              ? returnedData.enable_dual_pass
              : returnedData.enable_dual_pass === "true",
        }));
      }

      setSuccess(true);
      setError(null);
    } catch (err) {
      console.error("Failed to save LLM settings:", err.response ? err.response.data : err.message);
      let errorMessage = "Failed to save LLM settings.";
      if (err.response && err.response.data) {
        const errors = err.response.data;
        try {
          const messages = Object.entries(errors)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
            .join(" ");
          if (messages) {
            errorMessage += ` Details: ${messages}`;
          }
        } catch (parseError) {
          errorMessage += ` Raw error: ${JSON.stringify(err.response.data)}`;
        }
      } else if (err.message) {
        errorMessage += ` Error: ${err.message}`;
      }
      setError(errorMessage);
      setSuccess(false);
    }
  };

  // Import functions
  const handleOpenImportDialog = async () => {
    try {
      const response = await api.get("/api/user-companies-for-llm-import/");
      if (response.data.success) {
        // Filter out current company
        const companies = response.data.companies.filter(
          (company) => company.id.toString() !== companyId
        );
        setUserCompanies(companies);
        setImportDialogOpen(true);
      } else {
        setError(response.data.error || "Failed to load companies");
      }
    } catch (err) {
      console.error("Failed to load companies for import:", err);
      setError("Failed to load companies for import");
    }
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setSelectedSourceCompany("");
    setUserCompanies([]);
  };

  const handleImportSettings = async () => {
    if (!selectedSourceCompany || !companyId) {
      setError("Please select a source company");
      return;
    }

    setImportLoading(true);
    try {
      const response = await api.post("/api/import-llm-settings/", {
        source_company_id: selectedSourceCompany,
        target_company_id: companyId,
      });

      if (response.data.success) {
        setSuccess(true);
        setError(null);
        handleCloseImportDialog();

        // Reload settings to show imported data
        window.location.reload();
      } else {
        setError(response.data.error || "Failed to import settings");
      }
    } catch (err) {
      console.error("Failed to import LLM settings:", err);
      setError(err.response?.data?.error || "Failed to import settings");
    } finally {
      setImportLoading(false);
    }
  };

  const getModelVersionHelperText = (provider) => {
    switch (provider) {
      case "openai":
        return "e.g., gpt-4o-mini-2024-07-18, gpt-4-0125-preview";
      case "claude":
        return "e.g., claude-3-5-haiku-20241022, claude-3-opus-20240229";
      case "gemini":
        return "e.g., gemini-2.0-flash, gemini-1.5-pro-latest";
      default:
        return "";
    }
  };

  if (loading) {
    return <Loader message={t("loading")} fullscreen />;
  }

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Typography variant="h5" gutterBottom>
        {t("LLM Settings")}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
            {t("API Keys")}
            <InfoOutlined sx={{ ml: 1, fontSize: "1rem", color: "info.main" }} />
            <Typography variant="caption" sx={{ ml: 1, fontStyle: "italic" }}>
              ({t("shared across all companies")})
            </Typography>
          </Typography>

          <TextField
            fullWidth
            label={t("Azure Endpoint")}
            name="azure_endpoint"
            value={settings.azure_endpoint || ""}
            onChange={handleChange}
            margin="normal"
          />

          <TextField
            fullWidth
            label={t("Azure API Key")}
            name="azure_api_key"
            value={settings.azure_api_key || ""}
            onChange={handleChange}
            margin="normal"
            type="password"
          />

          <TextField
            fullWidth
            label={t("Content Understanding Endpoint")}
            name="content_understanding_endpoint"
            value={settings.content_understanding_endpoint || ""}
            onChange={handleChange}
            margin="normal"
            helperText={t("Foundry resource endpoint for Content Understanding")}
          />

          <TextField
            fullWidth
            label={t("Content Understanding API Key")}
            name="content_understanding_api_key"
            value={settings.content_understanding_api_key || ""}
            onChange={handleChange}
            margin="normal"
            type="password"
          />

          <TextField
            fullWidth
            label={t("Content Understanding Analyzer ID")}
            name="content_understanding_analyzer_id"
            value={settings.content_understanding_analyzer_id || ""}
            onChange={handleChange}
            margin="normal"
            helperText={t("Use prebuilt-invoice or your custom analyzer ID")}
          />

          <TextField
            fullWidth
            label={t("Content Understanding API Version")}
            name="content_understanding_api_version"
            value={settings.content_understanding_api_version || ""}
            onChange={handleChange}
            margin="normal"
            helperText="2025-11-01"
          />

          <TextField
            fullWidth
            label={t("Anthropic API Key")}
            name="anthropic_api_key"
            value={settings.anthropic_api_key || ""}
            onChange={handleChange}
            margin="normal"
            type="password"
          />

          <TextField
            fullWidth
            label={t("OpenAI API Key")}
            name="openai_api_key"
            value={settings.openai_api_key || ""}
            onChange={handleChange}
            margin="normal"
            type="password"
          />

          <TextField
            fullWidth
            label={t("Google API Key")}
            name="google_api_key"
            value={settings.google_api_key || ""}
            onChange={handleChange}
            margin="normal"
            type="password"
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t("Model Settings")}
          </Typography>

          <FormControl fullWidth margin="normal">
            <InputLabel>{t("Default Model Provider")}</InputLabel>
            <Select
              name="default_model"
              value={settings.default_model}
              onChange={handleChange}
              label={t("Default Model Provider")}
            >
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="claude">Anthropic Claude</MenuItem>
              <MenuItem value="gemini">Google Gemini</MenuItem>
              <MenuItem value="azure_content_understanding">Azure Content Understanding</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t("OpenAI Model Version")}
            name="openai_model_version"
            value={settings.openai_model_version || ""}
            onChange={handleChange}
            margin="normal"
            helperText={getModelVersionHelperText("openai")}
            disabled={settings.default_model !== "openai"}
          />

          <TextField
            fullWidth
            label={t("Claude Model Version")}
            name="claude_model_version"
            value={settings.claude_model_version || ""}
            onChange={handleChange}
            margin="normal"
            helperText={getModelVersionHelperText("claude")}
            disabled={settings.default_model !== "claude"}
          />

          <TextField
            fullWidth
            label={t("Gemini Model Version")}
            name="gemini_model_version"
            value={settings.gemini_model_version || ""}
            onChange={handleChange}
            margin="normal"
            helperText={getModelVersionHelperText("gemini")}
            disabled={settings.default_model !== "gemini"}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
            {t("Dual-Pass LLM Settings")}
            <InfoOutlined sx={{ ml: 1, fontSize: "1rem", color: "info.main" }} />
            <Typography variant="caption" sx={{ ml: 1, fontStyle: "italic" }}>
              ({t("Enhanced accuracy with two-stage processing")})
            </Typography>
          </Typography>

          <FormControl fullWidth margin="normal">
            <InputLabel>{t("Enable Dual-Pass Processing")}</InputLabel>
            <Select
              name="enable_dual_pass"
              value={settings.enable_dual_pass}
              onChange={handleChange}
              label={t("Enable Dual-Pass Processing")}
            >
              <MenuItem value={false}>{t("No (Use Single Model)")}</MenuItem>
              <MenuItem value={true}>{t("Yes (Use Dual-Pass)")}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" disabled={!settings.enable_dual_pass}>
            <InputLabel>{t("Primary Model (Pass 1)")}</InputLabel>
            <Select
              name="dual_pass_primary_model"
              value={settings.dual_pass_primary_model}
              onChange={handleChange}
              label={t("Primary Model (Pass 1)")}
            >
              <MenuItem value="claude">Anthropic Claude</MenuItem>
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="gemini">Google Gemini</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t("Primary Model Version")}
            name="dual_pass_primary_model_version"
            value={settings.dual_pass_primary_model_version || ""}
            onChange={handleChange}
            margin="normal"
            helperText={getModelVersionHelperText(settings.dual_pass_primary_model)}
            disabled={!settings.enable_dual_pass}
          />

          <FormControl fullWidth margin="normal" disabled={!settings.enable_dual_pass}>
            <InputLabel>{t("Validation Model (Pass 2)")}</InputLabel>
            <Select
              name="dual_pass_validation_model"
              value={settings.dual_pass_validation_model}
              onChange={handleChange}
              label={t("Validation Model (Pass 2)")}
            >
              <MenuItem value="claude">Anthropic Claude</MenuItem>
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="gemini">Google Gemini</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t("Validation Model Version")}
            name="dual_pass_validation_model_version"
            value={settings.dual_pass_validation_model_version || ""}
            onChange={handleChange}
            margin="normal"
            helperText={getModelVersionHelperText(settings.dual_pass_validation_model)}
            disabled={!settings.enable_dual_pass}
          />

          <TextField
            fullWidth
            multiline
            rows={8}
            label={t("Validation Custom Prompt")}
            name="dual_pass_validation_prompt"
            value={settings.dual_pass_validation_prompt || ""}
            onChange={handleChange}
            margin="normal"
            disabled={!settings.enable_dual_pass}
            helperText={t(
              "Custom prompt for validation pass. Leave empty to use default validation prompt."
            )}
            InputProps={{
              style: {
                fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
                fontSize: "15px",
                whiteSpace: "pre-wrap",
                lineHeight: "1.5",
                backgroundColor: "#f8f8f8",
                padding: "16px",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                tabSize: 2,
              },
            }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
            {t("Product Matching LLM Settings")}
            <InfoOutlined sx={{ ml: 1, fontSize: "1rem", color: "info.main" }} />
            <Typography variant="caption" sx={{ ml: 1, fontStyle: "italic" }}>
              ({t("AI model used for matching invoice items to warehouse products")})
            </Typography>
          </Typography>

          <FormControl fullWidth margin="normal">
            <InputLabel>{t("Product Matching Model")}</InputLabel>
            <Select
              name="product_match_model"
              value={settings.product_match_model || "openai"}
              onChange={handleChange}
              label={t("Product Matching Model")}
            >
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="claude">Anthropic Claude</MenuItem>
              <MenuItem value="gemini">Google Gemini</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t("Product Matching Model Version")}
            name="product_match_model_version"
            value={settings.product_match_model_version || ""}
            onChange={handleChange}
            margin="normal"
            helperText={getModelVersionHelperText(settings.product_match_model || "openai")}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
            {t("OpenAI Vision Settings")}
            <InfoOutlined sx={{ ml: 1, fontSize: "1rem", color: "info.main" }} />
            <Typography variant="caption" sx={{ ml: 1, fontStyle: "italic" }}>
              (
              {t("Directly process documents using OpenAI Vision instead of Azure Form Recognizer")}
              )
            </Typography>
          </Typography>

          <FormControl fullWidth margin="normal">
            <InputLabel>{t("Use OpenAI Vision API")}</InputLabel>
            <Select
              name="use_openai_vision"
              value={settings.use_openai_vision}
              onChange={handleChange}
              label={t("Use OpenAI Vision API")}
              disabled={settings.default_model !== "openai"}
            >
              <MenuItem value={false}>{t("No (Use Azure Form Recognizer)")}</MenuItem>
              <MenuItem value={true}>{t("Yes (Use OpenAI Vision API)")}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={10}
            label={t("OpenAI Vision Custom Prompt")}
            name="openai_vision_prompt"
            value={settings.openai_vision_prompt || ""}
            onChange={handleChange}
            margin="normal"
            disabled={settings.default_model !== "openai" || !settings.use_openai_vision}
            helperText={t(
              "Custom prompt for OpenAI Vision processing. Leave empty to use default prompt."
            )}
            InputProps={{
              style: {
                fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
                fontSize: "15px",
                whiteSpace: "pre-wrap",
                lineHeight: "1.5",
                backgroundColor: "#f8f8f8",
                padding: "16px",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                tabSize: 2,
              },
            }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t("Processing Instructions")}
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={25}
            label={t("LLM Instructions")}
            name="llm_instructions"
            value={settings.llm_instructions}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
            InputProps={{
              style: {
                fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
                fontSize: "15px",
                whiteSpace: "pre-wrap",
                lineHeight: "1.5",
                backgroundColor: "#f5f5f5",
                padding: "16px",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                tabSize: 2,
                overflowX: "auto",
              },
            }}
          />

          <TextField
            fullWidth
            multiline
            rows={20}
            label={t("Additional Information")}
            name="additional_info"
            value={settings.additional_info}
            onChange={handleChange}
            margin="normal"
            InputProps={{
              style: {
                fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
                fontSize: "15px",
                whiteSpace: "pre-wrap",
                lineHeight: "1.5",
                backgroundColor: "#f8f8f8",
                padding: "16px",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                tabSize: 2,
              },
            }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleOpenImportDialog}
            startIcon={<GetAppIcon />}
            disabled={loading}
          >
            {t("Import from Another Company")}
          </Button>

          <Button variant="contained" color="primary" type="submit" disabled={loading}>
            {t("Save Settings")}
          </Button>
        </Box>
      </form>

      {/* Import Settings Dialog */}
      <Dialog open={importDialogOpen} onClose={handleCloseImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t("Import LLM Settings")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {t(
              "Select a company to import LLM settings from. This will copy all API keys, model configurations, and prompts from the selected company."
            )}
          </Typography>

          {userCompanies.length === 0 ? (
            <Alert severity="info">{t("No other companies with LLM settings found")}</Alert>
          ) : (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>{t("Source Company")}</InputLabel>
              <Select
                value={selectedSourceCompany}
                onChange={(e) => setSelectedSourceCompany(e.target.value)}
                label={t("Source Company")}
              >
                {userCompanies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name} ({company.company_code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog}>{t("Cancel")}</Button>
          <Button
            onClick={handleImportSettings}
            variant="contained"
            disabled={!selectedSourceCompany || importLoading || userCompanies.length === 0}
          >
            {importLoading ? t("Importing...") : t("Import")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)}>
        <Alert severity="success">{t("Settings saved successfully!")}</Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Paper>
  );
};

LLMSettings.propTypes = {
  companyId: PropTypes.string.isRequired,
};

export default LLMSettings;
